import { useEffect, useState } from "react";
import { ArrowsClockwise } from "@phosphor-icons/react";

/**
 * WidgetHeaderMeta — V1.1.1 OP-002 · Dashboard Productivity.
 *
 * Small "refresh + last updated" block designed to sit in a widget's
 * header. Two responsibilities:
 *   1. Show a compact "Last Updated" relative timestamp (auto-refreshes
 *      the display every 30 seconds so "just now" doesn't stay forever).
 *   2. Expose a Refresh button that calls the caller-supplied handler.
 *
 * Any dashboard widget can adopt this in ~2 lines — kept intentionally
 * unopinionated about layout so it slots into different widget headers.
 *
 * Props:
 *   - lastUpdated: ISO string | Date | null
 *   - loading:     boolean — disables the refresh button and animates it
 *   - onRefresh:   () => void
 *   - testid:      base testid (e.g. "my-applications"); the button becomes
 *                  `${testid}-refresh` and the timestamp `${testid}-updated`.
 */
export default function WidgetHeaderMeta({ lastUpdated, loading, onRefresh, testid }) {
  // Re-render every 30s so the relative-time string ("just now" → "1 min ago")
  // stays visually fresh even when the widget itself doesn't reload.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="flex items-center gap-2 text-[11px] text-ink-secondary">
      {lastUpdated && (
        <span
          data-testid={`${testid}-updated`}
          title={new Date(lastUpdated).toLocaleString("en-CA", { hour12: false })}
        >
          Updated {relative(lastUpdated)}
        </span>
      )}
      <button
        data-testid={`${testid}-refresh`}
        onClick={onRefresh}
        disabled={loading}
        aria-label="Refresh"
        className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-divider bg-surface text-ink-secondary transition hover:bg-surface-alt disabled:opacity-50"
      >
        <ArrowsClockwise
          size={12}
          weight="bold"
          className={loading ? "animate-spin" : undefined}
        />
      </button>
    </div>
  );
}

function relative(iso) {
  const then = iso instanceof Date ? iso.getTime() : new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diff = Math.round((Date.now() - then) / 1000);
  if (diff < 45) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)} h ago`;
  return new Date(then).toLocaleDateString("en-CA", {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
}
